import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import { SubmitButton } from '../shared/SubmitButton';
import type { AssetCatalogItemEntity } from '@peritasai/repository';
import { AssetType, useCreateAssetCatalogItem, useUpdateAssetCatalogItem } from '@peritasai/repository';
import { TrayType } from '@peritasai/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';
import { formatAssetType } from '../../utils/formatAssetType';

export { DrawerMode as AssetCatalogDrawerMode };

interface AssetCatalogDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedItem: AssetCatalogItemEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  type: string;
  trayType: string;
  name: string;
  manufacturer: string;
  productNumber: string;
  description: string;
  isActive: boolean;
}

interface MetadataEntry {
  id: string;
  key: string;
  value: string;
}

interface FormErrors {
  type?: string;
  name?: string;
  manufacturer?: string;
  productNumber?: string;
  metadataKeys?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  type: '',
  trayType: '',
  name: '',
  manufacturer: '',
  productNumber: '',
  description: '',
  isActive: true,
};

const TRAY_TYPES = Object.values(TrayType);

const metadataToEntries = (meta: Record<string, unknown> | null | undefined): MetadataEntry[] => {
  if (!meta) return [];
  return Object.entries(meta).map(([key, value]) => ({ id: crypto.randomUUID(), key, value: String(value) }));
};

const buildMetadata = (entries: MetadataEntry[]): Record<string, string> | null => {
  const valid = entries.filter((e) => e.key.trim().length > 0);
  if (valid.length === 0) return null;
  return Object.fromEntries(valid.map((e) => [e.key.trim(), e.value]));
};

function validate(
  form: FormState,
  mode: DrawerMode,
  t: (key: string, options?: Record<string, string>) => string,
  metadataEntries: MetadataEntry[] = []
): FormErrors {
  const errors: FormErrors = {};
  if (mode === DrawerMode.Create) {
    if (!form.type) {
      errors.type = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.assetCatalogDrawer.typeLabel'),
      });
    }
    if (!form.name.trim()) {
      errors.name = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.assetCatalogDrawer.nameLabel'),
      });
    }
    if (!form.manufacturer.trim()) {
      errors.manufacturer = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.assetCatalogDrawer.manufacturerLabel'),
      });
    }
    if (!form.productNumber.trim()) {
      errors.productNumber = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.assetCatalogDrawer.productNumberLabel'),
      });
    }
  }
  const keys = metadataEntries.map((e) => e.key.trim()).filter((k) => k.length > 0);
  if (keys.length !== new Set(keys).size) {
    errors.metadataKeys = t('pages.entityManagement.assetCatalogDrawer.metadataDuplicateKeyError');
  }
  return errors;
}

export const AssetCatalogDrawer = ({
  open,
  mode,
  selectedItem,
  onClose,
  onSuccess,
}: AssetCatalogDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [metadataEntries, setMetadataEntries] = useState<MetadataEntry[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const createMutation = useCreateAssetCatalogItem();
  const updateMutation = useUpdateAssetCatalogItem();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const entityName = t('pages.entityManagement.assetCatalogDrawer.entityName');
  const isTrayType = form.type === String(AssetType.TRAY);
  const defaultTrayTypeOption = TrayType.HOSPITAL;

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedItem) {
      setForm({
        type: selectedItem.type ?? '',
        trayType: selectedItem.trayType ?? defaultTrayTypeOption,
        name: selectedItem.name ?? '',
        manufacturer: selectedItem.manufacturer ?? '',
        productNumber: selectedItem.productNumber ?? '',
        description: selectedItem.description ?? '',
        isActive: selectedItem.isActive,
      });
      setMetadataEntries(metadataToEntries(selectedItem.metadata));
    } else {
      setForm({...emptyForm, trayType: defaultTrayTypeOption });
      setMetadataEntries([]);
    }
    setErrors({});
    setTouched(false);
    setCreateSuccess(false);
  }, [open, mode, selectedItem]);

  const handleChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      setCreateSuccess(false);
      if (touched) {
        setErrors(validate(next, mode, t, metadataEntries));
      }
    };

  const handleSelectChange =
    (field: keyof FormState) => (e: SelectChangeEvent<string>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      setCreateSuccess(false);
      if (touched) {
        setErrors(validate(next, mode, t, metadataEntries));
      }
    };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, isActive: e.target.checked }));
    setCreateSuccess(false);
  };

  const handleAddMetadata = () => {
    setMetadataEntries((prev) => [...prev, { id: crypto.randomUUID(), key: '', value: '' }]);
    setCreateSuccess(false);
  };

  const handleMetadataKeyChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = metadataEntries.map((entry, i) =>
        i === index ? { ...entry, key: e.target.value } : entry
      );
      setMetadataEntries(next);
      setCreateSuccess(false);
      if (touched) {
        setErrors(validate(form, mode, t, next));
      }
    };

  const handleMetadataValueChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setMetadataEntries((prev) =>
        prev.map((entry, i) => (i === index ? { ...entry, value: e.target.value } : entry))
      );
      setCreateSuccess(false);
    };

  const handleDeleteMetadata = (index: number) => () => {
    const next = metadataEntries.filter((_, i) => i !== index);
    setMetadataEntries(next);
    setCreateSuccess(false);
    if (touched) {
      setErrors(validate(form, mode, t, next));
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    setCreateSuccess(false);
    const validationErrors = validate(form, mode, t, metadataEntries);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const metadata = buildMetadata(metadataEntries);

    const trayDetailsPayload =
      isTrayType && form.trayType
        ? { trayDetails: { trayType: form.trayType as TrayType } }
        : null;

    try {
      // Create keeps the drawer open so users can add multiple items in sequence.
      if (mode === DrawerMode.Create) {
        await createMutation.mutateAsync({
          type: form.type,
          name: form.name.trim(),
          manufacturer: form.manufacturer.trim(),
          productNumber: form.productNumber.trim(),
          ...(form.description.trim().length > 0 && { description: form.description.trim() }),
          ...(metadata !== null ? { metadata } : {}),
          ...trayDetailsPayload,
          isActive: form.isActive,
        });
        setForm(emptyForm);
        setMetadataEntries([]);
        setTouched(false);
        setCreateSuccess(true);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedItem) {
        await updateMutation.mutateAsync({
          id: selectedItem.id,
          dto: {
            name: form.name.trim(),
            ...(form.description.trim().length > 0
              ? { description: form.description.trim() }
              : { description: null }),
            metadata,
            isActive: form.isActive,
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  const isEdit = mode === DrawerMode.Edit;

  return (
    <SideDrawer
      open={open}
      title={
        isEdit
          ? t('pages.entityManagement.drawer.titleEdit', { entityName })
          : t('pages.entityManagement.drawer.titleCreate', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={6}>
          <FormControl
            fullWidth
            size="small"
            disabled={isEdit}
            error={touched && !!errors.type}
          >
            <InputLabel id="asset-catalog-type-label">
              {`${t('pages.entityManagement.assetCatalogDrawer.assetTypeLabel')}${!isEdit ? ' *' : ''}`}
            </InputLabel>
            <Select
              labelId="asset-catalog-type-label"
              value={form.type}
              label={`${t('pages.entityManagement.assetCatalogDrawer.assetTypeLabel')}${!isEdit ? ' *' : ''}`}
              onChange={handleSelectChange('type')}
            >
              {Object.values(AssetType).map((assetType) => (
                <MenuItem key={assetType} value={assetType}>
                  {formatAssetType(assetType)}
                </MenuItem>
              ))}
            </Select>
            {touched && errors.type && (
              <FormHelperText sx={{ ml: '5px' }}>{errors.type}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.assetCatalogDrawer.nameLabel')}${!isEdit ? ' *' : ''}`}
            fullWidth
            size="small"
            value={form.name}
            onChange={handleChange('name')}
            disabled={isEdit}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.assetCatalogDrawer.manufacturerLabel')}${!isEdit ? ' *' : ''}`}
            fullWidth
            size="small"
            value={form.manufacturer}
            onChange={handleChange('manufacturer')}
            disabled={isEdit}
            error={touched && !!errors.manufacturer}
            helperText={touched ? errors.manufacturer : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.assetCatalogDrawer.productNumberLabel')}${!isEdit ? ' *' : ''}`}
            fullWidth
            size="small"
            value={form.productNumber}
            onChange={handleChange('productNumber')}
            disabled={isEdit}
            error={touched && !!errors.productNumber}
            helperText={touched ? errors.productNumber : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        {isTrayType && (
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="asset-catalog-tray-type-label">
                {`${t('pages.entityManagement.assetCatalogDrawer.trayTypeLabel')}${!isEdit ? ' *' : ''}`}
              </InputLabel>
              <Select
                data-testid={"asset-catalog-tray-type"}
                labelId="asset-catalog-tray-type-label"
                value={form.trayType}
                disabled={isEdit}
                label={`${t('pages.entityManagement.assetCatalogDrawer.trayTypeLabel')}${!isEdit ? ' *' : ''}`}
                onChange={handleSelectChange('trayType')}
              >
                {TRAY_TYPES.map((trayType) => (
                  <MenuItem key={trayType} value={trayType}>
                    {t(`pages.entityManagement.assetCatalogDrawer.trayTypeOptions.${trayType}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid size={12}>
          <TextField
            label={t('pages.entityManagement.assetCatalogDrawer.descriptionLabel')}
            fullWidth
            size="small"
            value={form.description}
            onChange={handleChange('description')}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 0.5 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
              mb: metadataEntries.length > 0 ? 1 : 0,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {t('pages.entityManagement.assetCatalogDrawer.metadataLabel')}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddMetadata}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {t('pages.entityManagement.assetCatalogDrawer.metadataAddButton')}
            </Button>
          </Box>

          {metadataEntries.map((entry, index) => (
            <Box
              key={entry.id}
              sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
              data-testid={`metadata-row-${index}`}
            >
              <TextField
                placeholder={t('pages.entityManagement.assetCatalogDrawer.metadataKeyPlaceholder')}
                size="small"
                value={entry.key}
                onChange={handleMetadataKeyChange(index)}
                sx={{ flex: 1 }}
              />
              <TextField
                placeholder={t('pages.entityManagement.assetCatalogDrawer.metadataValuePlaceholder')}
                size="small"
                value={entry.value}
                onChange={handleMetadataValueChange(index)}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={handleDeleteMetadata(index)}
                aria-label={t('pages.entityManagement.assetCatalogDrawer.metadataDeleteButton')}
                data-testid={`metadata-delete-${index}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {touched && errors.metadataKeys && (
            <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
              {errors.metadataKeys}
            </Typography>
          )}
        </Grid>

        <Grid size={6}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
            >
              {t('pages.entityManagement.assetCatalogDrawer.activeLabel')}
            </Typography>
            <Switch
              checked={form.isActive}
              onChange={handleSwitchChange}
              color="primary"
              slotProps={{ input: { 'data-testid': 'active-switch' } as React.InputHTMLAttributes<HTMLInputElement> }}
              sx={{ ml: -1.5 }}
            />
          </Box>
        </Grid>
      </Grid>

      {createSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {t('pages.entityManagement.assetCatalogDrawer.createSuccess')}
        </Alert>
      )}

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
